import { Badge, Box, Flex, IconButton, Spinner, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr } from '@chakra-ui/react'
import { differenceInCalendarDays, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { RiAlertLine, RiCalendarEventLine, RiDeleteBinLine, RiEditLine, RiMoneyDollarCircleLine } from 'react-icons/ri'
import { FinancialRecurringPagination, FinancialRecurringTransaction } from '../../../Interfaces/financialRecurring'
import { Pagination } from '../../../components/Pagination'

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value ?? 0)

const formatDate = (value?: string | null) => {
  if (!value) {
    return '-'
  }

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number)
    // Criar data no timezone local (month - 1 porque Date usa 0-11 para meses)
    const date = new Date(year, month - 1, day)
    if (Number.isNaN(date.getTime())) {
      return '-'
    }
    return format(date, 'dd/MM/yyyy', { locale: ptBR })
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return '-'
  }

  return format(date, 'dd/MM/yyyy', { locale: ptBR })
}

const typeBadgeMap: Record<string, { label: string; colorScheme: string }> = {
  CREDITO: { label: 'C', colorScheme: 'green' },
  DEBITO: { label: 'D', colorScheme: 'red' },
  BOLETO: { label: 'B', colorScheme: 'purple' },
}

const getStatusLabel = (status: string, type?: string): string => {
  if (status === 'PAGO') {
    const isReceita = type === 'CREDITO' || type === 'BOLETO'
    return isReceita ? 'Recebido' : 'Pago'
  }
  return 'Em aberto'
}

const statusBadgeMap: Record<string, { label: string; colorScheme: string }> = {
  PAGO: { label: 'Pago', colorScheme: 'green' },
  EM_ABERTO: { label: 'Em aberto', colorScheme: 'orange' },
}

const categoryLabelMap: Record<string, string> = {
  EMPRESA: 'Empresa',
  PESSOAL: 'Pessoal',
  OUTROS: 'Outros',
}

interface TransactionsTableProps {
  data?: FinancialRecurringTransaction[]
  isLoading?: boolean
  pagination?: FinancialRecurringPagination
  onPageChange?: (page: number) => void
  onEdit: (transaction: FinancialRecurringTransaction) => void
  onMarkAsPaid: (transaction: FinancialRecurringTransaction) => void
  onDelete: (transaction: FinancialRecurringTransaction) => void
}

export function TransactionsTable({
  data = [],
  isLoading,
  pagination,
  onPageChange,
  onEdit,
  onMarkAsPaid,
  onDelete,
}: TransactionsTableProps) {
  const renderRow = (transaction: FinancialRecurringTransaction) => {
    const dueDate = transaction.due_date ? new Date(transaction.due_date) : null
    const movementDate = transaction.movement_date ? new Date(transaction.movement_date) : null
    const today = new Date()

    const daysUntilDue = dueDate ? differenceInCalendarDays(dueDate, today) : null
    const isPaid = transaction.status === 'PAGO'
    const isOverdue = !isPaid && typeof daysUntilDue === 'number' && daysUntilDue < 0
    const isDueSoon = !isPaid && typeof daysUntilDue === 'number' && daysUntilDue >= 0 && daysUntilDue <= 3

    const rowBg = isOverdue ? 'red.900' : isDueSoon ? 'orange.900' : undefined
    const iconColor = isOverdue ? 'red.300' : isDueSoon ? 'orange.300' : 'gray.500'

    const typeMeta = typeBadgeMap[transaction.type] ?? { label: transaction.type, colorScheme: 'gray' }
    const isReceita = transaction.type === 'CREDITO' || transaction.type === 'BOLETO'
    const statusLabel = getStatusLabel(transaction.status, transaction.type)
    const statusMeta = {
      label: statusLabel,
      colorScheme: statusBadgeMap[transaction.status]?.colorScheme ?? 'gray',
    }

    const paymentMethodName = transaction.paymentMethod?.name ?? '-'
    // Não permitir exclusão de lançamentos que têm ID de OS (gerados automaticamente)
    const canDelete = !transaction.id_os

    const clientLabel = transaction.client?.name || transaction.client?.company_name || 'Cliente não informado'

    const recurrenceLabel = (() => {
      // Se não é recorrente, retorna '-'
      if (!transaction.is_recurring) return '-'
      
      // Se é recorrente mas não tem grupo, mostra apenas que é recorrente
      if (!transaction.recurrence_group_id) {
        return 'Recorrente'
      }
      
      // Se tem grupo, mostra a sequência
      const current = transaction.sequence ?? 1
      const totalValue = transaction.recurrence_total
      if (totalValue === undefined || totalValue === null) {
        return `${current}/∞`
      }
      return `${current}/${totalValue}`
    })()

    const rowKey = transaction.financial_transaction_id ?? transaction.id

    return (
      <Tr key={rowKey} bg={rowBg} _hover={{ bg: rowBg ? rowBg : 'gray.700' }}>
        <Td>
          <Flex align="center" gap={2}>
            {(isOverdue || isDueSoon) && (
              <Tooltip label={isOverdue ? 'Lançamento vencido' : 'Vencimento próximo'}>
                <Box color={iconColor}>{isOverdue ? <RiAlertLine /> : <RiCalendarEventLine />}</Box>
              </Tooltip>
            )}
            <Text fontWeight="bold">{transaction.document_number}</Text>
          </Flex>
        </Td>
        <Td>
          <Badge colorScheme={typeMeta.colorScheme}>{typeMeta.label}</Badge>
        </Td>
        <Td>{transaction.id_os ?? '-'}</Td>
        <Td>{categoryLabelMap[transaction.category] ?? transaction.category}</Td>
        <Td>
          <Text>{clientLabel}</Text>
        </Td>
        <Td>{movementDate ? formatDate(transaction.movement_date) : '-'}</Td>
        <Td>{dueDate ? formatDate(transaction.due_date) : '-'}</Td>
        <Td>{transaction.payment_date ? formatDate(transaction.payment_date) : '-'}</Td>
        <Td>{formatCurrency(transaction.value)}</Td>
        <Td>{transaction.interest_value ? formatCurrency(transaction.interest_value) : '-'}</Td>
        <Td>{transaction.paid_value ? formatCurrency(transaction.paid_value) : '-'}</Td>
        <Td>
          <Badge colorScheme={statusMeta.colorScheme}>{statusMeta.label}</Badge>
        </Td>
        <Td>{paymentMethodName}</Td>
        <Td>{recurrenceLabel}</Td>
        <Td>
          <Flex gap={2}>
            <Tooltip label="Editar lançamento">
              <IconButton
                aria-label="Editar lançamento"
                size="sm"
                icon={<RiEditLine />}
                onClick={() => onEdit(transaction)}
              />
            </Tooltip>
            {!isPaid && (
              <Tooltip label={isReceita ? 'Marcar como recebido' : 'Marcar como pago'}>
                <IconButton
                  aria-label={isReceita ? 'Marcar como recebido' : 'Marcar como pago'}
                  size="sm"
                  colorScheme="green"
                  icon={<RiMoneyDollarCircleLine />}
                  onClick={() => onMarkAsPaid(transaction)}
                />
              </Tooltip>
            )}
            <Tooltip 
              label={
                canDelete 
                  ? "Excluir lançamento" 
                  : "Não é possível excluir lançamentos gerados automaticamente a partir de ordens de serviço"
              }>
              <IconButton
                aria-label="Excluir lançamento"
                size="sm"
                colorScheme="red"
                icon={<RiDeleteBinLine />}
                onClick={() => onDelete(transaction)}
                isDisabled={!canDelete}
                opacity={canDelete ? 1 : 0.5}
              />
            </Tooltip>
          </Flex>
        </Td>
      </Tr>
    )
  }

  if (isLoading) {
    return (
      <Flex justify="center" align="center" py={10}>
        <Spinner />
      </Flex>
    )
  }

  if (!data.length) {
    return (
      <Box textAlign="center" py={10} color="gray.400">
        Nenhum lançamento recorrente encontrado.
      </Box>
    )
  }

  return (
    <Box bg="gray.800" borderRadius="md" p={4}>
      <Table variant="simple" colorScheme="whiteAlpha">
        <Thead>
          <Tr>
            <Th>Nº Documento</Th>
            <Th>Tipo</Th>
            <Th>Nº OS</Th>
            <Th>Categoria</Th>
            <Th>Cliente</Th>
            <Th>Data de movimento</Th>
            <Th>Data de vencimento</Th>
            <Th>Pagamento</Th>
            <Th>Valor original</Th>
            <Th>Juros/Multa</Th>
            <Th>Valor pago</Th>
            <Th>Situação</Th>
            <Th>Forma de pagamento</Th>
            <Th>Recorrência</Th>
            <Th textAlign="center">Ações</Th>
          </Tr>
        </Thead>
        <Tbody>{data.map(renderRow)}</Tbody>
      </Table>

      {pagination && onPageChange && (
        <Pagination
          totalCountOfRegisters={pagination.total}
          registersPerPage={pagination.per_page}
          currentPage={pagination.current_page}
          onPageChange={onPageChange}
        />
      )}
    </Box>
  )
}
